import {
  Grid,
  Link,
  Typography,
  Box,
  Container,
  IconButton,
  Divider,
  Stack,
  Tooltip,
} from "@mui/material";

import {
  Facebook,
  X as XIcon,
  YouTube,
  KeyboardArrowUp,
  Language,
} from "@mui/icons-material";

const exploreLinks = [
  { label: "Dashboard", href: "/dashboard" },
  { label: "TV Slideshow", href: "/slide-show" },
  { label: "Features", sectionId: "features" },
  { label: "Key Messages", sectionId: "key messages" },
  { label: "Contact Us", sectionId: "contact" },
];

const quickLinks = [
  { label: "Ministry of Health", href: "https://moh.gov.et" },
  { label: "ERMP", href: "http://ermp.moh.gov.et" },
  { label: "PTS", href: "http://pts.moh.gov.et" },
  { label: "CPD", href: "https://cpd.moh.gov.et" },
  { label: "LIPH", href: "http://196.188.120.145:8000" },
];

const socialLinks = [
  {
    label: "Facebook",
    href: "https://www.facebook.com/EthiopiaFMoH",
    icon: <Facebook />,
  },
  { label: "X (Twitter)", href: "https://twitter.com/fmohealth", icon: <XIcon /> },
  {
    label: "YouTube",
    href: "https://www.youtube.com/@FMoHealthEthiopia",
    icon: <YouTube />,
  },
  { label: "Website", href: "https://moh.gov.et", icon: <Language /> },
];

const linkSx = {
  color: "rgba(255, 255, 255, 0.8)",
  display: "inline-block",
  transition: "color 0.2s, transform 0.2s",
  "&:hover": { color: "common.white", transform: "translateX(4px)" },
};

const headingSx = {
  fontWeight: 600,
  mb: 2,
  textTransform: "uppercase",
  letterSpacing: 1,
  fontSize: "0.9rem",
};

// Scroll to a section if it is on the current page, otherwise go to the home page
// and let Home.js scroll to it from the URL hash.
function handleSectionClick(event, sectionId) {
  const element = document.getElementById(sectionId);
  if (element) {
    event.preventDefault();
    element.scrollIntoView({ behavior: "smooth" });
  }
}

function LinkColumn({ title, links }) {
  return (
    <>
      <Typography variant="h6" sx={headingSx}>
        {title}
      </Typography>
      <Stack component="ul" spacing={1.25} sx={{ listStyle: "none", m: 0, p: 0 }}>
        {links.map(({ label, href, sectionId }) => {
          const external = href?.startsWith("http");
          return (
            <li key={label}>
              <Link
                href={sectionId ? `/#${sectionId}` : href}
                onClick={
                  sectionId
                    ? (event) => handleSectionClick(event, sectionId)
                    : undefined
                }
                underline="none"
                variant="body2"
                sx={linkSx}
                {...(external && { target: "_blank", rel: "noopener noreferrer" })}
              >
                {label}
              </Link>
            </li>
          );
        })}
      </Stack>
    </>
  );
}

export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        background: (theme) =>
          `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
        color: "common.white",
        pt: { xs: 6, md: 8 },
        pb: 3,
        mt: 8,
      }}
    >
      <Container>
        <Grid container spacing={{ xs: 4, md: 6 }}>
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                bgcolor: "common.white",
                borderRadius: 2,
                px: 2,
                py: 1.5,
                display: "inline-block",
                mb: 2,
              }}
            >
              <Box
                component="img"
                src="/moh.png"
                alt="Ministry of Health - Ethiopia"
                sx={{ display: "block", width: { xs: 220, sm: 260 }, maxWidth: "100%" }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255, 255, 255, 0.85)", maxWidth: 380, lineHeight: 1.7 }}
            >
              Our vision is to see healthy, productive, and prosperous citizens
              of Ethiopia.
            </Typography>
          </Grid>

          <Grid item xs={6} md={2}>
            <LinkColumn title="Explore" links={exploreLinks} />
          </Grid>

          <Grid item xs={6} md={2}>
            <LinkColumn title="Quick Links" links={quickLinks} />
          </Grid>

          <Grid item xs={12} md={3}>
            <Typography variant="h6" sx={headingSx}>
              Connect With Us
            </Typography>
            <Stack direction="row" spacing={1}>
              {socialLinks.map(({ label, href, icon }) => (
                <Tooltip key={label} title={label}>
                  <IconButton
                    component="a"
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    sx={{
                      color: "common.white",
                      bgcolor: "rgba(255, 255, 255, 0.12)",
                      transition: "background-color 0.2s, transform 0.2s",
                      "&:hover": {
                        bgcolor: "rgba(255, 255, 255, 0.25)",
                        transform: "translateY(-3px)",
                      },
                    }}
                  >
                    {icon}
                  </IconButton>
                </Tooltip>
              ))}
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.2)", mt: 6, mb: 3 }} />

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          sx={{ textAlign: { xs: "center", sm: "left" } }}
        >
          <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.75)" }}>
            © {year}{" "}
            <Link
              color="inherit"
              href="https://moh.gov.et/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Ministry of Health - Ethiopia
            </Link>
            . All rights reserved. Powered by{" "}
            <Link
              color="inherit"
              href="https://habtechsolution.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              HABTech Solutions
            </Link>
          </Typography>

          <Tooltip title="Back to top">
            <IconButton
              aria-label="Back to top"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              size="small"
              sx={{
                color: "common.white",
                border: "1px solid rgba(255, 255, 255, 0.4)",
                "&:hover": { bgcolor: "rgba(255, 255, 255, 0.15)" },
              }}
            >
              <KeyboardArrowUp />
            </IconButton>
          </Tooltip>
        </Stack>
      </Container>
    </Box>
  );
}
